import {
    FetchState,
    Advisor3Rule,
    AdvisorRulePreviewResult,
    AdvisorPreviewTemplateResponse
} from "@/shared/types";
import {
    AdvisorState,
    AdvisorAction,
    ActionMediaParams
} from "../types";
import * as advisor3Service from "@/shared/api/advisor3Service";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: AdvisorState = {
    actionFetchState: FetchState.Uninitialized,
    action: null,
    actions: [],
    actionFetchAllState: FetchState.Uninitialized,
    forceListRefresh: false,
    ruleFetchAllState: FetchState.Uninitialized,
    ruleFetchState: FetchState.Uninitialized,
    rulePreviewFetchState: FetchState.Uninitialized,
    rule: null,
    rules: [],
    previewResult: { data: null, error: null },
    previewTemplateFetchState: FetchState.Uninitialized,
    previewTemplateResult: []
};

export const useAdvisorStore = defineStore("advisor", {
    state: (): AdvisorState => (_.cloneDeep(initialState)),
    getters: {
        isActionLoaded: state => state.actionFetchState === FetchState.Loaded,
        forceRefresh: state => state.forceListRefresh,
        rulePreviewResult: state => state.previewResult,
        isPreviewLoaded: state => state.rulePreviewFetchState === FetchState.Loaded,
        isPreviewTemplateLoaded: state => state.previewTemplateFetchState === FetchState.Loaded,
    },
    actions: {
        setAction (action: AdvisorAction) {
            this.action = action;
        },
        setActions (actions: AdvisorAction[]) {
            return this.actions = actions;
        },
        setActionFetchState (fetchState: FetchState) {
            this.actionFetchState = fetchState;
        },
        setActionFetchAllState (f: FetchState) {
            return this.actionFetchAllState = f;
        },
        setRule (rule: Advisor3Rule) {
            return this.rule = rule;
        },
        setRules (rules: Advisor3Rule[]) {
            return this.rules = rules;
        },
        setFetchState (f: FetchState) {
            return this.ruleFetchState = f;
        },
        setFetchAllState (f: FetchState) {
            return this.ruleFetchAllState = f;
        },
        setForceRefresh (f: boolean) {
            return this.forceListRefresh = f;
        },
        setPreviewFetchState (f: FetchState) {
            return this.rulePreviewFetchState = f;
        },
        setRulePreview (rulePreviewResult: AdvisorRulePreviewResult) {
            return this.previewResult = rulePreviewResult;
        },
        setPreviewTemplateFetchState (f: FetchState) {
            return this.previewTemplateFetchState = f;
        },
        setPreviewTemplateResponse (testTemplates: AdvisorPreviewTemplateResponse[]) {
            return this.previewTemplateResult = testTemplates;
        },
        resetPreviewTemplateResult () {
            return this.previewTemplateResult = [];
        },
        loadRule (ruleId: number): Promise<any> {
            return advisor3Service.get(ruleId)
                .then(result => {
                    if (!result.data) {
                        return Promise.resolve(false);
                    }

                    this.setRule(result.data);
                    return Promise.resolve(result.data);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadAllRules (): Promise<void> {
            this.setFetchAllState(FetchState.Loading);
            return new Promise(resolve => {
                return advisor3Service.getAll()
                    .then(r => {
                        this.setRules(r.data);
                        this.setFetchAllState(FetchState.Loaded);
                        this.setForceRefresh(false);
                        resolve();
                    })
                    .catch(err => {
                        this.setFetchAllState(FetchState.Loaded);
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
            });
        },
        saveRule (r: Advisor3Rule): Promise<any> {
            this.setFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return new Promise((resolve, reject) => {
                return advisor3Service.save(r)
                    .then(resp => {
                        if (!resp.data) {
                            resolve(false);
                            return;
                        }

                        r.id = resp.data as number;
                        this.setRule(r);
                        this.setForceRefresh(true);
                        notifierStore.setSuccess("Rule saved.");
                        resolve(r.id);
                    });
            })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                })
                .finally(() => this.setFetchState(FetchState.Loaded));
        },
        softDelete (id: number): Promise<any> {
            return new Promise<any>(resolve => {
                return advisor3Service.softDeleteRule(id)
                    .then(r => {
                        if (!r || !r.data) {
                            return resolve(false);
                        }

                        this.setForceRefresh(true);
                        return resolve(true);
                    })
                    .catch(err => {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
            });
        },
        previewRule (r: Advisor3Rule): Promise<any> {
            this.setPreviewFetchState(FetchState.Loading);

            return new Promise((resolve, reject) => {
                return advisor3Service.previewRule(r)
                    .then(resp => {
                        if (!resp.data) {
                            resolve(false);
                            return;
                        }

                        this.setPreviewFetchState(FetchState.Loaded);
                        this.setRulePreview(resp.data);
                        resolve(resp.data);
                    });
            })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                })
                .finally(() => this.setPreviewFetchState(FetchState.Loaded));
        },
        loadAction (actionId: number) {
            this.setActionFetchState(FetchState.Loading);
            return advisor3Service.getAction(actionId)
                .then(resp => {
                    advisor3Service.getMedia(actionId)
                        .then(files => {
                                resp.data.mediaFileNames = files.data;
                                this.setAction(resp.data);
                                this.setActionFetchState(FetchState.Loaded);
                            }
                        );
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        saveAction (action: AdvisorAction): Promise<any> {
            const notifierStore = useNotifierStore();
            return advisor3Service.saveAction(action)
                .then(resp => {
                    notifierStore.setSuccess("Advisor action saved.");
                    return resp.data;
                })
                .then(id => {
                    this.addActionMedia({
                        files: action.mediaFiles,
                        actionId: id,
                    });
                    return id;
                }).then(id => {
                    this.loadAllActions();
                    return id;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        addActionMedia ({ files, actionId }) {
            if (files && files.length) {
                return advisor3Service.saveMedia(actionId, files)
                    .then(() => {
                        this.loadAction(actionId);
                    });
            }
        },
        mediaRemove (params: ActionMediaParams): Promise<any> {
            const attachmentName = params.fileName;
            const actionId = params.actionId;
            return advisor3Service.removeMedia(params.actionId, attachmentName)
                .then(response => {
                    if (!response.data) {
                        window.console.error(`Could not remove media ${attachmentName}` +
                            ` from action with id ${params.actionId}`);
                        return;
                    }
                    this.loadAction(actionId);
                    return actionId;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        mediaGetLink (params: ActionMediaParams) {
            const attachmentName = params.fileName;
            const newTab = window.open();
            return advisor3Service.mediaGetDownloadLink(params.actionId, attachmentName).then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        deleteAction (actionId: number): Promise<any> {
            return new Promise<any>(resolve => {
                return advisor3Service.deleteAction(actionId)
                    .then(r => {
                        if (!r || !r.data) {
                            return resolve(false);
                        }
                        this.loadAllActions();
                        return resolve(true);
                    })
                    .catch(err => {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
            });
        },
        render (data: { actionId: number, ruleExecutionId: number }): Promise<string> {
            return new Promise<string>(resolve => {
                return advisor3Service.render(data.actionId, data.ruleExecutionId)
                    .then(r => resolve(r.data));
            });
        },
        previewTemplate (data: { actionId: number, ruleId: number }): Promise<any> {
            this.setPreviewTemplateFetchState(FetchState.Loading);
            return new Promise(resolve => {
                return advisor3Service.previewTemplate(data.actionId, data.ruleId)
                    .then(r => {
                            this.setPreviewTemplateFetchState(FetchState.Loaded);
                            this.setPreviewTemplateResponse(r.data);
                            resolve(r.data);
                        }
                    );
            });
        },
        loadAllActions (): Promise<void> {
            this.setActionFetchAllState(FetchState.Loading);
            return new Promise(resolve => {
                return advisor3Service.getAllActions()
                    .then(r => {
                        this.setActions(r.data);
                        this.setActionFetchAllState(FetchState.Loaded);
                        this.setForceRefresh(false);
                        resolve();
                    })
                    .catch(err => {
                        this.setActionFetchAllState(FetchState.Loaded);
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
            });
        }
    }
});

